import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight, User, Users, ClipboardList, Gauge, Award, Plus, Trash2, Save } from "lucide-react";

const AERO_ROWS = ["Pre-Solo Air Legislation", "T/A Solo Air Legislation", "BAK", "NAV", "Radio", "CTA/CTR", "PPL Theory"];
const FLYING_ROWS = ["ELP", "First Flight", "First Solo", "First T/A Solo", "RPL", "First Solo NAV", "PPL", "NVFR"];

const seedRows = (labels, existing) =>
  existing && existing.length
    ? existing
    : labels.map((l) => ({ id: crypto.randomUUID(), label: l, date: "", certified_by: "", custom: false }));

const Field = ({ label, value, onChange, type = "text", placeholder, testid, hint }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label} {hint && <span className="normal-case text-slate-400 font-normal">{hint}</span>}</Label>
    <Input type={type} step={type === "number" ? "0.1" : undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" placeholder={placeholder} data-testid={testid} />
  </div>
);

const SectionHead = ({ icon: Icon, children }) => (
  <p className="text-xs font-bold uppercase tracking-wider text-lime-600 mb-3 flex items-center gap-1.5"><Icon className="h-4 w-4" /> {children}</p>
);

function MilestoneTable({ title, rows, setRows, testid }) {
  const upd = (id, key, val) => setRows(rows.map((r) => (r.id === id ? { ...r, [key]: val } : r)));
  const add = () => setRows([...rows, { id: crypto.randomUUID(), label: "", date: "", certified_by: "", custom: true }]);
  const del = (id) => setRows(rows.filter((r) => r.id !== id));
  return (
    <div data-testid={testid}>
      <p className="text-xs font-semibold text-slate-600 mb-2">{title}</p>
      <div className="space-y-1.5">
        <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">
          <span className="col-span-5">Subject / Event</span><span className="col-span-3">Date</span><span className="col-span-3">Certified by</span><span className="col-span-1" />
        </div>
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center" data-testid={`${testid}-row-${r.id}`}>
            {r.custom ? (
              <Input value={r.label} onChange={(e) => upd(r.id, "label", e.target.value)} placeholder="Custom milestone" className="sm:col-span-5 h-9" />
            ) : (
              <span className="sm:col-span-5 text-sm text-slate-700 px-1">{r.label}</span>
            )}
            <Input type="date" value={r.date || ""} onChange={(e) => upd(r.id, "date", e.target.value)} className="sm:col-span-3 h-9" data-testid={`${testid}-date-${r.id}`} />
            <Input value={r.certified_by || ""} onChange={(e) => upd(r.id, "certified_by", e.target.value)} placeholder="Instructor" className="sm:col-span-3 h-9" data-testid={`${testid}-cert-${r.id}`} />
            <div className="sm:col-span-1 flex justify-end">
              {r.custom && <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => del(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={add} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" /> Add milestone</Button>
    </div>
  );
}

export default function StudentDetails({ trainee, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(() => ({
    address: trainee.address || "",
    phone_other: trainee.phone_other || "",
    next_of_kin: trainee.next_of_kin || { name: "", relationship: "", address: "", phone_mobile: "", phone_other: "", email: "" },
    medical_examiner: trainee.medical_examiner || "",
    prev_training_orgs: trainee.prev_training_orgs || "",
    prev_records_received: trainee.prev_records_received || "",
    hours_last_12m: trainee.hours_last_12m || "",
    last_flight: trainee.last_flight || "",
    ac_types_flown: trainee.ac_types_flown || "",
    flying_summary: trainee.flying_summary || {},
    aero_knowledge: seedRows(AERO_ROWS, trainee.aero_knowledge),
    flying_training: seedRows(FLYING_ROWS, trainee.flying_training),
    certificates: trainee.certificates || { flight_test: {}, cert: {} },
    flight_test_result: trainee.flight_test_result || "pending",
  }));

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNok = (k, v) => setF((p) => ({ ...p, next_of_kin: { ...p.next_of_kin, [k]: v } }));
  const setFly = (k, v) => setF((p) => ({ ...p, flying_summary: { ...p.flying_summary, [k]: v } }));
  const setFt = (k, v) => setF((p) => ({ ...p, certificates: { ...p.certificates, flight_test: { ...(p.certificates.flight_test || {}), [k]: v } } }));
  const setCert = (k, v) => setF((p) => ({ ...p, certificates: { ...p.certificates, cert: { ...(p.certificates.cert || {}), [k]: v } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/training/trainees/${trainee.id}`, {
        first_name: trainee.first_name, last_name: trainee.last_name, ...f,
      });
      toast.success("Student details saved");
      onSaved?.();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid="student-details">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50 transition-colors" data-testid="student-details-toggle" aria-expanded={open}>
        {open ? <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
        <ClipboardList className="h-5 w-5 text-lime-500 shrink-0" />
        <div className="flex-1">
          <h2 className="font-heading font-bold text-lg text-slate-900">Student personal details &amp; training record</h2>
          <p className="text-xs text-slate-500 mt-0.5">CASA Form 4B08 — personal, next of kin, credentials, flying summary &amp; milestones</p>
        </div>
      </button>

      {open && (
        <div className="px-5 pb-6 pt-4 border-t border-slate-100 space-y-7" data-testid="student-details-body">
          {/* Personal */}
          <div>
            <SectionHead icon={User}>Personal details</SectionHead>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Address" value={f.address} onChange={(v) => set("address", v)} testid="sd-address" placeholder="Residential address" />
              <Field label="Phone (other)" value={f.phone_other} onChange={(v) => set("phone_other", v)} testid="sd-phone-other" placeholder="Landline / alt" />
            </div>
          </div>

          {/* Next of kin */}
          <div>
            <SectionHead icon={Users}>Next of kin</SectionHead>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name" value={f.next_of_kin.name} onChange={(v) => setNok("name", v)} testid="sd-nok-name" />
              <Field label="Relationship" value={f.next_of_kin.relationship} onChange={(v) => setNok("relationship", v)} testid="sd-nok-rel" />
              <Field label="Address" value={f.next_of_kin.address} onChange={(v) => setNok("address", v)} testid="sd-nok-address" />
              <Field label="Email" value={f.next_of_kin.email} onChange={(v) => setNok("email", v)} testid="sd-nok-email" />
              <Field label="Phone (mobile)" value={f.next_of_kin.phone_mobile} onChange={(v) => setNok("phone_mobile", v)} testid="sd-nok-mobile" />
              <Field label="Phone (other)" value={f.next_of_kin.phone_other} onChange={(v) => setNok("phone_other", v)} testid="sd-nok-other" />
            </div>
          </div>

          {/* Credentials */}
          <div>
            <SectionHead icon={Award}>Credentials &amp; past training experience</SectionHead>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Medical examiner" value={f.medical_examiner} onChange={(v) => set("medical_examiner", v)} testid="sd-med-examiner" />
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Previous records received?</Label>
                <Select value={f.prev_records_received || ""} onValueChange={(v) => set("prev_records_received", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="sd-prev-records"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                </Select>
              </div>
              <Field label="Previous training organisation/s" value={f.prev_training_orgs} onChange={(v) => set("prev_training_orgs", v)} testid="sd-prev-orgs" />
              <Field label="Hours last 12 months" value={f.hours_last_12m} onChange={(v) => set("hours_last_12m", v)} testid="sd-hours-12m" />
              <Field label="Last flight" type="date" value={f.last_flight} onChange={(v) => set("last_flight", v)} testid="sd-last-flight" />
              <Field label="A/C types flown" value={f.ac_types_flown} onChange={(v) => set("ac_types_flown", v)} testid="sd-ac-types" />
            </div>
          </div>

          {/* Flying summary */}
          <div>
            <SectionHead icon={Gauge}>Previous flying summary (hours)</SectionHead>
            <p className="text-xs font-semibold text-slate-600 mb-2">All flying</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Field label="PIC day" type="number" value={f.flying_summary.pic_day} onChange={(v) => setFly("pic_day", v)} testid="sd-fly-pic-day" />
              <Field label="PIC night" type="number" value={f.flying_summary.pic_night} onChange={(v) => setFly("pic_night", v)} testid="sd-fly-pic-night" />
              <Field label="Dual day" type="number" value={f.flying_summary.dual_day} onChange={(v) => setFly("dual_day", v)} testid="sd-fly-dual-day" />
              <Field label="Dual night" type="number" value={f.flying_summary.dual_night} onChange={(v) => setFly("dual_night", v)} testid="sd-fly-dual-night" />
              <Field label="Total" type="number" value={f.flying_summary.total} onChange={(v) => setFly("total", v)} testid="sd-fly-total" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-3">
              <Field label="NAV dual X/C" type="number" value={f.flying_summary.nav_dual_xc} onChange={(v) => setFly("nav_dual_xc", v)} testid="sd-fly-nav-dual" />
              <Field label="NAV PIC X/C" type="number" value={f.flying_summary.nav_pic_xc} onChange={(v) => setFly("nav_pic_xc", v)} testid="sd-fly-nav-pic" />
              <Field label="A/C I.F." type="number" value={f.flying_summary.inst_ac_if} onChange={(v) => setFly("inst_ac_if", v)} testid="sd-fly-ac-if" />
              <Field label="SIM I.F." type="number" value={f.flying_summary.inst_sim_if} onChange={(v) => setFly("inst_sim_if", v)} testid="sd-fly-sim-if" />
              <Field label="Total I.F." type="number" value={f.flying_summary.inst_total_if} onChange={(v) => setFly("inst_total_if", v)} testid="sd-fly-total-if" />
            </div>
          </div>

          {/* Milestones */}
          <div className="space-y-5">
            <SectionHead icon={Award}>Training milestones</SectionHead>
            <MilestoneTable title="Aeronautical knowledge" rows={f.aero_knowledge} setRows={(r) => set("aero_knowledge", r)} testid="sd-aero" />
            <MilestoneTable title="Flying training" rows={f.flying_training} setRows={(r) => set("flying_training", r)} testid="sd-flying" />
          </div>

          {/* Certificates */}
          <div>
            <SectionHead icon={Award}>Certificates</SectionHead>
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Flight test outcome</Label>
              <Select value={f.flight_test_result} onValueChange={(v) => set("flight_test_result", v)}>
                <SelectTrigger className="h-9 w-48" data-testid="sd-flight-test-result"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-slate-400">Set to “Passed” to unlock certificate generation.</span>
            </div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Flight test certificate — required vs actual (hours)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 self-end">Requirement</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Required</div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Actual</div>
              {[["Total time", "total"], ["Pilot in command", "pic"], ["Cross country PIC", "xc_pic"]].map(([lbl, key]) => (
                <div key={key} className="contents">
                  <div className="text-sm text-slate-700 self-center">{lbl}</div>
                  <Input type="number" step="0.1" value={f.certificates.flight_test?.[`${key}_required`] ?? ""} onChange={(e) => setFt(`${key}_required`, e.target.value)} data-testid={`sd-ft-${key}-req`} className="h-9" />
                  <Input type="number" step="0.1" value={f.certificates.flight_test?.[`${key}_actual`] ?? ""} onChange={(e) => setFt(`${key}_actual`, e.target.value)} data-testid={`sd-ft-${key}-act`} className="h-9" />
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <Field label="Flight test issued by" value={f.certificates.flight_test?.issued_by} onChange={(v) => setFt("issued_by", v)} testid="sd-ft-issued-by" />
              <Field label="Flight test date" type="date" value={f.certificates.flight_test?.date} onChange={(v) => setFt("date", v)} testid="sd-ft-date" />
              <div />
              <Field label="Certification issued by" value={f.certificates.cert?.issued_by} onChange={(v) => setCert("issued_by", v)} testid="sd-cert-issued-by" />
              <Field label="Certification signatory" value={f.certificates.cert?.signature} onChange={(v) => setCert("signature", v)} testid="sd-cert-sig" />
              <Field label="Certification date" type="date" value={f.certificates.cert?.date} onChange={(v) => setCert("date", v)} testid="sd-cert-date" />
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold" data-testid="save-student-details">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save student details"}
          </Button>
        </div>
      )}
    </div>
  );
}
